import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	DOCUMENT,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
	Signal,
	viewChild,
} from '@angular/core';
import { outputToObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { Actions, ofActionCompleted, ofActionDispatched, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BehaviorSubject, debounceTime, map, Observable, Subject, switchMap, take, takeUntil } from 'rxjs';

import {
	Account,
	AnalyticsEvent,
	Contact,
	ContactRole,
	ENTER,
	ENTERPRISE_EDITION_CODE,
	Location,
	NUMPAD_ENTER,
	Permissions,
	SPACE,
} from '@efficiently/models';

import { UserInformation } from '@efficiently/client-api-auth';
import { AnalyticsService } from '@efficiently/client-core-analytics';
import { AppConfigService } from '@efficiently/client-core-services';
import {
	CoreSelectors,
	SetPageTitleAction,
	SetUserAbbreviationAction,
	SuccessUploadAvatarAction,
	UpdateProfileInfoAction,
	UploadAvatarAction,
} from '@efficiently/client-domain-core';
import {
	ChangeEmailAction,
	GetOrgAccount,
	OrgAdminSelectors,
	ResetPasswordAction,
} from '@efficiently/client-domain-org-admin';
import { PermissionSelectors } from '@efficiently/client-domain-permissions';
import {
	AnimationDirective,
	AnimationDS,
	AnimationEnum,
	DIALOG_MAX_HEIGHT,
	DisableOnlySpacesValidatorDirective,
	ImageDirective,
	REQUIRED_VALIDATOR_ERROR,
	SEARCH_DEBOUNCE_MS,
	SMALL_DIALOG_WIDTH,
} from '@efficiently/client-ui-common';
import { ConfirmDialog, ConfirmDialogComponent, ConfirmModalResponse } from '@efficiently/client-ui-confirm-dialog';
import { EffIconComponent } from '@efficiently/client-ui-icons';
import { PageTitleModel } from '@efficiently/client-util-classes';
import { ValueLabelOption } from '@efficiently/client-util-types';
import { PermissionsDirective } from '@efficiently/client-widget-permissions';
import { SnackbarService } from '@efficiently/client-widget-snackbar';

import { HeaderService } from '@ds/core/components/header/header.service';
import { OrgAdminChangeEmailComponent } from '@ds/pages/org-admin/org-admin-general/org-admin-edit-user/org-admin-change-email';
import { McpGqlService } from '@ds/shared/graphql/mcp/mcp-gql.service';
import { AvatarTypes, AvatarUploadData } from '@ds/shared/modules/avatar/avatar.models';
import { AvatarUploaderComponent } from '@ds/shared/modules/avatar/avatar-uploader/avatar-uploader.component';
import { TelInputComponent } from '@ds/shared/modules/tel-input';

const CROPPER_SIZE = 222;
const MCP_KEY_GENERATION_ERROR_MESSAGE = 'Could not generate the MCP API key. Please try again.';

@Component({
	selector: 'ds-my-profile',
	templateUrl: './my-profile.component.html',
	styleUrls: ['./my-profile.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		EffIconComponent,
		AsyncPipe,
		FormsModule,
		ReactiveFormsModule,
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatFormField,
		MatLabel,
		MatInput,
		MatError,
		MatIconButton,
		MatButton,
		MatOption,
		MatSelect,
		DisableOnlySpacesValidatorDirective,
		ImageDirective,
		PermissionsDirective,
		AnimationDirective,
		TelInputComponent,
	],
})
export class MyProfileComponent implements OnInit, OnDestroy {
	@SelectSnapshot(OrgAdminSelectors.account) public account: Account;

	@Select(OrgAdminSelectors.locations) public locations$!: Observable<Location[]>;
	@Select(OrgAdminSelectors.account) public account$!: Observable<Account>;

	private destroy$: Subject<void> = new Subject<void>();
	private avatarDialogRef: MatDialogRef<AvatarUploaderComponent>;

	private readonly dialog = inject(MatDialog);
	private readonly snackbarService = inject(SnackbarService);
	private readonly mcpGqlService = inject(McpGqlService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly store = inject(Store);
	private readonly dialogRef = inject(MatDialogRef<MyProfileComponent>);
	private readonly actions$ = inject(Actions);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly headerService = inject(HeaderService);
	private readonly appConfigService = inject(AppConfigService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly document = inject(DOCUMENT);
	private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

	private readonly blockRoleSelectionKeys = (event: KeyboardEvent): void => {
		const isEnter = event.code === ENTER || event.code === NUMPAD_ENTER;
		const isSpaceOutsideSearch = event.code === SPACE && event.target !== this.searchInput()?.nativeElement;
		if (isEnter || isSpaceOutsideSearch) {
			event.preventDefault();
			event.stopPropagation();
		}
	};

	public readonly Permissions = Permissions;
	public mcpKeyPrefix: string | null = null;
	public mcpRawKey: string | null = null;

	protected readonly searchPlaceholder: string = 'Search';
	protected readonly maxLengthField: number = 32;

	public formGroup: FormGroup = new FormGroup({
		firstName: new FormControl(''),
		lastName: new FormControl(''),
		email: new FormControl(''),
		phone: new FormControl(''),
		avatarUrl: new FormControl(''),
		locationId: new FormControl(''),
		jobTitle: new FormControl(''),
		roles: new FormControl([]),
	});
	public searchForm: FormGroup = new FormGroup({
		search: new FormControl(''),
	});
	public readonly searchTerm: Signal<string> = toSignal(
		this.searchForm.controls.search.valueChanges.pipe(debounceTime(SEARCH_DEBOUNCE_MS)),
		{
			initialValue: '',
		},
	);
	public avatarUrl: string;
	public isImageLoaded$ = new BehaviorSubject<boolean>(true);
	public isAvatarLoaded: boolean = false;
	public skeletonAnimation: AnimationDS = { enabled: true, animationType: AnimationEnum.loading };
	public userLocations: Array<{ value: string; label: string }>;
	public userInfo: UserInformation;
	public orgNumber: string;
	public specialEdition: string;
	public title: PageTitleModel;
	public userInfoInitials: string;
	public REQUIRED_VALIDATOR_ERROR = REQUIRED_VALIDATOR_ERROR;
	public readonly filteredRoles: Signal<ValueLabelOption[]> = computed(() => {
		const search = this.searchTerm()?.toLowerCase();
		return !search ? this.roles : this.roles.filter(role => role.label.toLowerCase().includes(search));
	});

	public readonly roles: ValueLabelOption[] = Object.values(ContactRole).map(role => ({
		value: role,
		label: role,
	}));

	public get isPhotoAdded(): boolean {
		return !!this.avatarUrl;
	}

	public ngOnInit(): void {
		this.orgNumber = this.appConfigService.config.org?.orgNumber;
		if (this.appConfigService.config.org?.registrationSettingId === ENTERPRISE_EDITION_CODE) {
			this.specialEdition = ENTERPRISE_EDITION_CODE;
		}
		const storedLocations: Location[] = this.store.selectSnapshot(OrgAdminSelectors.locations);
		this.title = this.store.selectSnapshot(CoreSelectors.title);

		if (!storedLocations?.length) {
			this.store.dispatch(new GetOrgAccount());
		}
		if (this.store.selectSnapshot(PermissionSelectors.hasPermission(Permissions.Mcp))) {
			this.mcpGqlService
				.getMcpApiKeyPrefix()
				.pipe(takeUntil(this.destroy$))
				.subscribe(prefix => {
					this.mcpKeyPrefix = prefix;
					this.cdr.markForCheck();
				});
		}

		this.initUserInfo();

		this.orgNumber = this.appConfigService.config.org.orgNumber;
		const userContact = this.userInfo.contact;
		this.locations$
			.pipe(
				map(locations => {
					if (userContact) {
						this.userLocations = locations.reduce((filteredLocations, location) => {
							if (location._id === userContact.locationId) {
								return filteredLocations.concat({ value: location._id, label: location.name });
							}

							return filteredLocations;
						}, []);

						if (this.userLocations?.length) {
							this.formGroup.controls.locationId.patchValue(this.userLocations[0].value);
						}
						this.handleDisabledControls();
					}
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();

		if (userContact?.avatarUrl) {
			this.avatarUrl = userContact?.avatarUrl;
		}
		this.handleDisabledControls();
	}

	public ngOnDestroy(): void {
		this.store.dispatch(new SetPageTitleAction(this.title));
		this.destroy$.next();
		this.destroy$.complete();
		this.document.removeEventListener('keydown', this.blockRoleSelectionKeys, true);
	}

	public handleUploadYourPhoto(): void {
		const userContact = this.userInfo.contact;
		const data: AvatarUploadData = {
			avatarType: AvatarTypes.PHOTO,
			parentId: userContact._id,
			objectType: 'Contact',
			avatarUrl: this.avatarUrl,
			cropperStaticWidth: CROPPER_SIZE,
			cropperStaticHeight: CROPPER_SIZE,
			isRoundCropper: true,
			hideResizeSquares: true,
			roundResizeStyle: true,
			imageTypes: ['png', 'webp', 'jpg', 'jpeg'],
		};
		this.avatarDialogRef = this.dialog.open(AvatarUploaderComponent, {
			width: SMALL_DIALOG_WIDTH,
			maxHeight: DIALOG_MAX_HEIGHT,
			disableClose: false,
			data,
		});

		this.avatarDialogRef
			.afterOpened()
			.pipe(
				switchMap(() => this.actions$.pipe(ofActionDispatched(UploadAvatarAction))),
				takeUntilDestroyed(this.destroyRef),
				take(1),
			)
			.subscribe(() => {
				this.isImageLoaded$.next(false);
			});

		this.avatarDialogRef
			.afterClosed()
			.pipe(
				switchMap(() => this.actions$.pipe(ofActionSuccessful(SuccessUploadAvatarAction))),
				takeUntilDestroyed(this.destroyRef),
				take(1),
			)
			.subscribe(resp => {
				this.avatarUrl = resp.content.thumbnailUrl;
				this.formGroup.get('avatarUrl').patchValue(resp.content.thumbnailUrl);
				this.isImageLoaded$.next(true);
			});

		this.avatarDialogRef
			.afterClosed()
			.pipe(
				switchMap(() => this.actions$.pipe(ofActionCompleted(UploadAvatarAction))),
				takeUntilDestroyed(this.destroyRef),
				take(1),
			)
			.subscribe(() => {
				this.isImageLoaded$.next(true);

				const contact: Contact = { ...userContact, avatarUrl: this.formGroup.get('avatarUrl').value };

				this.store.dispatch(new UpdateProfileInfoAction(contact));
			});

		outputToObservable(this.avatarDialogRef.componentInstance.onDelete)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.avatarUrl = null;
				this.formGroup.get('avatarUrl').patchValue(null);
				const contact: Contact = { ...userContact, avatarUrl: null };
				this.isImageLoaded$.next(true);

				this.store.dispatch(new UpdateProfileInfoAction(contact));
			});
	}

	public handleResetPassword(): void {
		const userEmail = this.formGroup?.get('email')?.value;
		const data: ConfirmDialog = {
			message: `A reset password link will be sent to ${userEmail}`,
			cancelText: 'Cancel',
			confirmText: 'Send Reset link',
			title: 'Reset Password',
			isButtonsCentered: true,
			isMessageCentered: true,
		};
		this.dialog
			.open(ConfirmDialogComponent, {
				data,
				disableClose: true,
				autoFocus: false,
				width: '450px',
				panelClass: 'ds-confirm-dialog--custom',
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe((resp: ConfirmModalResponse) => {
				if (resp.isConfirmed) {
					this.snackbarService.success(`Reset password link sent to ${userEmail}.`, {
						duration: 5000,
					});
					this.dialogRef.close();
					this.store.dispatch(new ResetPasswordAction({ email: userEmail }));
				}
			});
	}

	public handleChangeEmail(): void {
		this.dialog
			.open(OrgAdminChangeEmailComponent, {
				disableClose: true,
				width: SMALL_DIALOG_WIDTH,
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe((newEmail: string) => {
				if (newEmail) {
					const previousEmail = this.userInfo.contact.email;
					this.store.dispatch(new ChangeEmailAction({ previousEmail, newEmail }));
					this.analyticsService.track(AnalyticsEvent.ChangeEmail, {
						previousEmail,
						currentEmail: newEmail,
					});
					this.dialogRef.close();
				}
			});
	}

	public handleSave(): void {
		if (this.formGroup.invalid) {
			return;
		}
		const formValue = this.formGroup.getRawValue();
		formValue.firstName = formValue.firstName?.trim();
		formValue.lastName = formValue.lastName?.trim();
		const contact = {
			...this.userInfo.contact,
			...formValue,
			roles: formValue.roles || [],
		};
		this.store.dispatch([new SetUserAbbreviationAction(contact), new UpdateProfileInfoAction(contact)]);
		this.headerService.profileUpdatesMessage();
		this.dialogRef.close();
	}

	public handleCancel(): void {
		this.dialogRef.close();
	}

	public handleGenerateMcpKey(): void {
		this.mcpGqlService
			.generateMcpApiKey()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ({ rawKey, prefix }) => {
					this.mcpRawKey = rawKey;
					this.mcpKeyPrefix = prefix;
					this.cdr.markForCheck();
				},
				error: () => {
					this.snackbarService.error(MCP_KEY_GENERATION_ERROR_MESSAGE);
				},
			});
	}

	public handleRegenerateMcpKey(): void {
		const data: ConfirmDialog = {
			message:
				'The current API key will be permanently invalidated. Any integrations using it will stop working.',
			cancelText: 'Cancel',
			confirmText: 'Regenerate',
			title: 'Regenerate MCP API Key',
			isButtonsCentered: true,
			isMessageCentered: true,
		};
		this.dialog
			.open(ConfirmDialogComponent, {
				data,
				disableClose: true,
				autoFocus: false,
				width: '450px',
				panelClass: 'ds-confirm-dialog--custom',
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe((resp: ConfirmModalResponse) => {
				if (resp.isConfirmed) {
					this.handleGenerateMcpKey();
				}
			});
	}

	public handleCopyMcpKey(): void {
		navigator.clipboard.writeText(this.mcpRawKey);
		this.snackbarService.success('API key copied to clipboard.', { duration: 3000 });
	}

	public getPhoneFormControl(): FormControl {
		return this.formGroup.get('phone') as FormControl;
	}

	public belongsToOneLocation(): boolean {
		const userContact = this.userInfo.contact;
		return this.userLocations?.length === 1 && !!userContact.locationId;
	}

	public handleAvatarLoaded(): void {
		this.isAvatarLoaded = true;
	}

	private handleDisabledControls(): void {
		this.formGroup.controls.email.disable();
		if (this.belongsToOneLocation()) {
			this.formGroup.controls.locationId.disable();
		} else {
			this.formGroup.controls.locationId.enable();
		}
	}

	private getUserInfoInitials(): string {
		return `${this.userInfo.contact.firstName.charAt(0)}${this.userInfo.contact.lastName.charAt(0)}`;
	}

	public clearSearch(): void {
		this.searchForm.controls.search.reset();
	}

	protected onRolesPanelOpened(): void {
		this.document.addEventListener('keydown', this.blockRoleSelectionKeys, true);
	}

	protected onRolesPanelClosed(): void {
		this.clearSearch();
		this.document.removeEventListener('keydown', this.blockRoleSelectionKeys, true);
	}

	private initUserInfo(): void {
		const userInfo = this.store.selectSnapshot(CoreSelectors.userInfo);
		this.userInfo = userInfo;
		this.userInfoInitials = this.getUserInfoInitials();
		this.formGroup.patchValue(
			{
				...userInfo.contact,
				roles: userInfo.contact.roles || [],
			},
			{ emitEvent: false, onlySelf: true },
		);
	}
}
